import * as React from "react";
import {
	Box,
	Button,
	Card,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	OutlinedInput,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { PencilSimpleIcon } from "@phosphor-icons/react/dist/ssr/PencilSimple";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { SparkleIcon } from "@phosphor-icons/react/dist/ssr/Sparkle";
import { TrashIcon } from "@phosphor-icons/react/dist/ssr/Trash";

import { provingGroundKeys, Scenario, useDeleteScenario, useSaveScenario } from "@/hooks/api/use-proving-ground";
import type { CalendarSimulation } from "@/hooks/api/use-proving-ground";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { useDialog } from "@/hooks/use-dialog";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column, FilterOption, FilterType } from "@/components/customTable/types";
import { DeleteModal } from "@/components/deleteModal/delete-modal";
import { TableAnimation } from "@/components/table-animation/table-animation";

import { GenerateDialog } from "../generate-dialog";
import { CalendarEditor } from "./calendar-editor";

function ScenarioDialog({
	scenario,
	open,
	onClose,
}: {
	scenario: Scenario | null;
	open: boolean;
	onClose: () => void;
}): React.JSX.Element {
	const save = useSaveScenario();
	const [name, setName] = React.useState("");
	const [situation, setSituation] = React.useState("");
	const [contact, setContact] = React.useState("");
	const [contactPhone, setContactPhone] = React.useState("");
	const [contactError, setContactError] = React.useState<string | null>(null);
	const [calendar, setCalendar] = React.useState<CalendarSimulation | null>(null);

	React.useEffect(() => {
		if (!open) return;
		setName(scenario?.name ?? "");
		setSituation(scenario?.situation ?? "");
		setContact(JSON.stringify(scenario?.contact ?? { Name: "", Email: "" }, null, 2));
		setContactPhone(scenario?.contactPhone ?? "");
		setContactError(null);
		setCalendar(scenario?.calendar ?? null);
	}, [open, scenario]);

	const submit = async () => {
		let parsed: Record<string, any> = {};
		try {
			parsed = contact.trim() ? JSON.parse(contact) : {};
		} catch {
			setContactError("That is not valid JSON.");
			return;
		}
		await save.mutateAsync({
			id: scenario?.id,
			name,
			situation,
			contact: parsed,
			contactPhone: contactPhone || null,
			calendar,
		});
		onClose();
	};

	return (
		<Dialog open={open} onClose={save.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
			<DialogTitle>{scenario ? "Edit scenario" : "New scenario"}</DialogTitle>
			<DialogContent>
				<Stack spacing={2.5} sx={{ mt: 1 }}>
					<FormControl fullWidth required>
						<InputLabel>Name</InputLabel>
						<OutlinedInput
							value={name}
							onChange={(event) => setName(event.target.value)}
							placeholder="e.g. Wants a price before committing"
						/>
					</FormControl>

					<TextField
						label="What the caller wants"
						value={situation}
						onChange={(event) => setSituation(event.target.value)}
						placeholder="Interested, but focused on price. Push hard on cost before agreeing to anything."
						helperText="Describes the goal, not the person. Every persona you select will pursue this."
						multiline
						minRows={3}
						required
						fullWidth
					/>

					<TextField
						label="Contact details"
						value={contact}
						onChange={(event) => setContact(event.target.value)}
						error={Boolean(contactError)}
						// Agent prompts are templates; without these the agent reads {{contact__Email}} aloud.
						helperText={
							contactError ??
							"Fills the {{contact__*}} placeholders in the agent's prompt. Leave these out and the agent reads the raw placeholder out loud."
						}
						multiline
						minRows={4}
						fullWidth
						slotProps={{ input: { sx: { fontFamily: "monospace", fontSize: ".85rem" } } }}
					/>

					<CalendarEditor value={calendar} onChange={setCalendar} />
					<FormControl fullWidth>
						<InputLabel>Contact phone</InputLabel>
						<OutlinedInput
							value={contactPhone}
							onChange={(event) => setContactPhone(event.target.value)}
							placeholder="+60123456789"
						/>
					</FormControl>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} color="secondary" disabled={save.isPending}>
					Cancel
				</Button>
				<Button onClick={submit} variant="contained" disabled={!name || !situation || save.isPending}>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export function ProvingGroundScenarios(): React.JSX.Element {
	const {
		items: scenarios,
		isLoading,
		totalItems,
		refetchData,
	} = useCachedTableStateWithUrl<Scenario>({
		queryKey: provingGroundKeys.scenarios(),
		endpoint: "/proving-ground/scenarios",
	});

	const remove = useDeleteScenario();
	const [editing, setEditing] = React.useState<Scenario | null>(null);
	const [dialogOpen, setDialogOpen] = React.useState(false);
	const [generateOpen, setGenerateOpen] = React.useState(false);

	const {
		data: pendingDelete,
		open: deleteOpen,
		handleClose: closeDelete,
		handleOpen: openDelete,
	} = useDialog<Scenario | null>();

	const edit = (scenario: Scenario | null) => {
		setEditing(scenario);
		setDialogOpen(true);
	};

	const filtersOptions: FilterOption[] = React.useMemo(
		() => [
			{
				name: "agentId",
				label: "Built for",
				value: "",
				type: FilterType.FETCH_SELECT,
				getOptionsUrl: "/agents",
				fetchLabel: "name",
				fetchValue: "id",
			},
		],
		[]
	);

	const columns: Column[] = React.useMemo(
		() => [
			{
				header: { id: "agentName", label: "Built for", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "agentName",
				getCellValue: (row: any) =>
					row.agentName ? (
						<Chip size="small" variant="outlined" label={row.agentName} />
					) : (
						<Chip size="small" variant="outlined" color="default" label="General" />
					),
			},
			{
				header: { id: "name", label: "Scenario", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "name",
				getCellValue: (row: any) => (
					<Box
						tabIndex={0}
						onClick={() => edit(row)}
						sx={{ cursor: "pointer", width: "max-content", "&:hover": { textDecoration: "underline" } }}
					>
						{row.name}
					</Box>
				),
			},
			{
				header: { id: "situation", label: "What they want", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "situation",
				getCellValue: (row: any) => (
					<Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
						{row.situation?.length > 160 ? `${row.situation.slice(0, 160)}…` : row.situation}
					</Typography>
				),
			},
			{
				header: { id: "actions", label: "", width: 100, align: "left", sx: { fontWeight: "600" } },
				accessorKey: "actions",
				getCellValue: (row: any) => (
					<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
						<TableActions
							actions={[
								{
									label: "Edit",
									icon: <PencilSimpleIcon width={20} height={20} weight="fill" />,
									onClick: () => edit(row),
								},
								{
									label: "Delete",
									icon: <TrashIcon width={20} height={20} weight="fill" />,
									sx: { color: "error.main" },
									onClick: () => openDelete(row),
								},
							]}
						/>
					</Box>
				),
			},
		],
		[]
	);

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ alignItems: { sm: "center" } }}>
						<Box sx={{ flex: "1 1 auto" }}>
							<Typography variant="h4">Scenarios</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								What a caller is trying to accomplish. Every persona you run will pursue each of these.
							</Typography>
						</Box>
						<Stack direction="row" spacing={1}>
							<Button startIcon={<SparkleIcon />} variant="outlined" onClick={() => setGenerateOpen(true)}>
								Generate
							</Button>
							<Button startIcon={<PlusIcon />} variant="contained" onClick={() => edit(null)}>
								New scenario
							</Button>
						</Stack>
					</Stack>
					<TableAnimation delay={450}>
						<Card>
							<CustomTable
								columns={columns}
								data={scenarios}
								refetchData={refetchData}
								isLoading={isLoading}
								totalItems={totalItems}
								filtersOptions={filtersOptions}
							/>
						</Card>
					</TableAnimation>
				</Stack>
			</Box>

			<ScenarioDialog
				scenario={editing}
				open={dialogOpen}
				onClose={() => {
					setDialogOpen(false);
					refetchData();
				}}
			/>

			<GenerateDialog
				kind="scenarios"
				open={generateOpen}
				onClose={() => setGenerateOpen(false)}
				onSaved={refetchData}
			/>

			<DeleteModal
				open={deleteOpen}
				onClose={closeDelete}
				onDelete={async () => {
					if (pendingDelete) await remove.mutateAsync(pendingDelete.id);
					closeDelete();
					refetchData();
				}}
				title="Delete scenario"
				description="Runs that already used it keep their results."
			/>
		</React.Fragment>
	);
}
